import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import { AutocompleteInput } from 'components/common/AdressMap/AutocompleteInput/AutocompleteInput.component'
import { FeatureFlag } from 'utils/featureFlags'
import { GRAY_900 } from 'constants/styling/theme'
import { Label } from 'components/common/Label'
import { MUIInputField } from 'components/common/MUIInputField'
import { ManualAddressCard } from '../ManualAddressCard'
import { ReactGeocodePlace } from 'react-geocode'
import { Stack } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'

export interface Props {
  /** Is address valid */
  addressIsValid: boolean
  /** Action called when address changes */
  handleMapOnChange: (place: ReactGeocodePlace | null, isInitialLookup: boolean) => void
  /** Selected place */
  selectedPlace: ReactGeocodePlace | null
  /** Function to update selected place */
  setSelectedPlace: (place: ReactGeocodePlace | null) => void
}

/**
 * @component Create or update property component in Address step
 * @example <CreateUpdateProperty />
 */
export const CreateUpdateProperty: React.FC<Props> = ({
  addressIsValid,
  handleMapOnChange,
  selectedPlace,
  setSelectedPlace
}) => {
  const { t } = useTranslation(['order'], { keyPrefix: 'step_address' })
  const allowManualAddress = useFlag(FeatureFlag.ALLOW_MANUAL_ADDRESS)

  const {
    propertyName,
    setPropertyName,
    isAddressManually,
    isUpdatePropertyAddressFlow,
    setIsAddressManually,
  } = usePurchaseFlowRealEstateProperty()

  const [showError, setShowError] = useState<boolean>(false)

  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)
  }

  useEffect(() => {
    logAnalyticsEvent(isAddressManually ? AnalyticsEvent.MANUAL_ADDRESS_SELECTED : AnalyticsEvent.MANUAL_ADDRESS_UNSELECTED, {})
  }, [isAddressManually])

  return (
    <Stack gap="1.6rem">

      <MUIInputField
        label={t('property_name')}
        placeholder={t('property_name_placeholder')}
        value={propertyName}
        isError={showError && !propertyName}
        onChange={(e) => setPropertyName(e.target.value)}
        onBlur={handleOnInputBlur}
      />

      {!isAddressManually &&
        <Stack gap=".6rem">
          <Label text={t('property_address')} />

          <AutocompleteInput
            disabled={isAddressManually}
            inputPosition="below"
            handleChange={(newGooglePlace, isInitialLookup) => handleMapOnChange(newGooglePlace, isInitialLookup)}
            onInputChange={e => setSelectedPlace(null)}
            error={(selectedPlace && !addressIsValid && !isAddressManually) || (!selectedPlace && isUpdatePropertyAddressFlow) ? t('address_not_selected') : undefined}
            inputStyles={{ fontSize: '1.4rem' }}
          />

          <Label
            text={
              <Trans t={t} i18nKey="manual_address_hint">
                <span
                  style={{ color: GRAY_900, cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => setIsAddressManually(true)}
                >
                  &nbsp;
                </span>
              </Trans>
            }
          />
        </Stack>
      }

      {allowManualAddress && isAddressManually &&
        <ManualAddressCard setSelectedPlace={setSelectedPlace} />
      }

    </Stack>
  )
}
